import React, { useState, createContext, useContext, useEffect } from "react";
import { useApiService } from "../shared/apiservice";
import { User } from "../shared/types";

export type SortOrder = "asc" | "desc";

export type CreateUserPayload = {
    email: string;
    name: string;
    password: string;
    password_confirmation: string;
};

const usersListContext = createContext<{
    users: User[];
    errors: string[];
    loading: boolean;
    creating: boolean;
    page: number;
    perPage: number;
    total: number;
    searchTerm: string;
    sortField: string;
    sortOrder: SortOrder;
    fetchUsers: (overridePage?: number) => void;
    createUser: (payload: CreateUserPayload) => Promise<{ ok: true } | { ok: false; message: string }>;
    setPage: (page: number) => void;
    setPerPage: (perPage: number) => void;
    setSearchTerm: (term: string) => void;
    setSort: (field: string) => void;
}>({
    users: [],
    errors: [],
    loading: false,
    creating: false,
    page: 1,
    perPage: 15,
    total: 0,
    searchTerm: "",
    sortField: "name",
    sortOrder: "asc",
    fetchUsers: () => { },
    createUser: async () => ({ ok: false, message: "" }),
    setPage: () => { },
    setPerPage: () => { },
    setSearchTerm: () => { },
    setSort: () => { },
});

const userDetailContext = createContext<{
    user: User | null;
    errors: string[];
    loading: boolean;
    saving: boolean;
    savingPassword: boolean;
    passwordError: string | null;
    fetchUser: () => void;
    saveUser: (updates: Partial<Pick<User, "name" | "email">> & Record<string, unknown>) => Promise<boolean>;
    updatePassword: (password: string, passwordConfirmation: string) => Promise<boolean>;
}>({
    user: null,
    errors: [],
    loading: false,
    saving: false,
    savingPassword: false,
    passwordError: null,
    fetchUser: () => { },
    saveUser: async () => false,
    updatePassword: async () => false,
});

function parseUser(d: any): User {
    return {
        id: d.id,
        name: d.name,
        email: d.email,
        functions: Array.isArray(d.functions) ? d.functions : [],
    };
}

function useProviderUsers() {
    const [users, setUsers] = useState<User[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [page, setPageInternal] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [total, setTotal] = useState(0);
    const [searchTerm, setSearchTermInternal] = useState("");
    const [sortField, setSortField] = useState("name");
    const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
    const apiService = useApiService();

    function setSearchTerm(term: string) {
        setSearchTermInternal(term);
        setPageInternal(1);
    }

    function setPage(p: number) {
        setPageInternal(p);
    }

    function setSort(field: string) {
        if (field === sortField) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
        setPageInternal(1);
    }

    useEffect(() => {
        fetchUsers();
    }, [page, perPage, searchTerm, sortField, sortOrder]);

    async function fetchUsers(overridePage?: number) {
        const pageToFetch = overridePage ?? page;
        setLoading(true);
        setErrors([]);

        try {
            const params = new URLSearchParams({
                page: String(pageToFetch),
                per_page: String(perPage),
                sort: sortField,
                order: sortOrder,
            });
            if (searchTerm.trim()) {
                params.set("q", searchTerm.trim());
            }
            const response = await apiService.get(`/users?${params.toString()}`);
            if (!response.ok) {
                setErrors(["Loading users failed. Please try again."]);
                return;
            }
            const json = await response.json();
            const data: User[] = (json.data ?? json).map(parseUser);
            setTotal(json.meta?.total ?? data.length);
            setUsers(data);
        } catch (error) {
            console.error(error);
            setErrors(["Loading users failed. Please try again."]);
        } finally {
            setLoading(false);
        }
    }

    async function createUser(
        payload: CreateUserPayload
    ): Promise<{ ok: true } | { ok: false; message: string }> {
        setCreating(true);
        try {
            const response = await apiService.post(`/users/store`, payload);
            if (!response.ok) {
                let message = "Could not create user. Please try again.";
                try {
                    const body = await response.json();
                    if (typeof body.message === "string") {
                        message = body.message;
                    } else if (body.errors && typeof body.errors === "object") {
                        const first = Object.values(body.errors)[0];
                        message = Array.isArray(first) ? first[0] : String(first);
                    }
                } catch {
                    /* keep default */
                }
                return { ok: false, message };
            }
            setPageInternal(1);
            await fetchUsers(1);
            return { ok: true };
        } catch (error) {
            console.error(error);
            return { ok: false, message: "Could not create user. Please try again." };
        } finally {
            setCreating(false);
        }
    }

    return {
        users,
        errors,
        loading,
        creating,
        page,
        perPage,
        total,
        searchTerm,
        sortField,
        sortOrder,
        fetchUsers,
        createUser,
        setPage,
        setPerPage,
        setSearchTerm,
        setSort,
    };
}

function useProviderUserDetail(userId: number | null | undefined) {
    const [user, setUser] = useState<User | null>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savingPassword, setSavingPassword] = useState(false);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const apiService = useApiService();

    async function fetchUser() {
        if (!userId) return;
        setLoading(true);
        setErrors([]);

        try {
            const response = await apiService.get(`/users/${userId}`);
            if (!response.ok) {
                setErrors(["Loading user failed. Please try again."]);
                return;
            }
            const json = await response.json();
            setUser(parseUser(json.data ?? json));
        } catch (error) {
            console.error(error);
            setErrors(["Loading user failed. Please try again."]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (userId) {
            fetchUser();
        } else {
            setUser(null);
            setErrors([]);
        }
    }, [userId]);

    async function saveUser(
        updates: Partial<Pick<User, "name" | "email">> & Record<string, unknown>
    ): Promise<boolean> {
        if (!userId) return false;
        setSaving(true);
        setErrors([]);

        try {
            const response = await apiService.post(`/users/${userId}/update`, updates);
            if (!response.ok) {
                setErrors(["Saving user failed. Please try again."]);
                return false;
            }
            const json = await response.json();
            setUser(parseUser(json.data ?? json));
            return true;
        } catch (error) {
            console.error(error);
            setErrors(["Saving user failed. Please try again."]);
            return false;
        } finally {
            setSaving(false);
        }
    }

    async function updatePassword(
        password: string,
        passwordConfirmation: string
    ): Promise<boolean> {
        if (!userId) return false;
        setSavingPassword(true);
        setPasswordError(null);

        try {
            const response = await apiService.post(`/users/${userId}/update`, {
                password,
                password_confirmation: passwordConfirmation,
            });
            if (!response.ok) {
                setPasswordError("Failed to update password. Please try again.");
                return false;
            }
            return true;
        } catch (error) {
            console.error(error);
            setPasswordError("Failed to update password. Please try again.");
            return false;
        } finally {
            setSavingPassword(false);
        }
    }

    return {
        user,
        errors,
        loading,
        saving,
        savingPassword,
        passwordError,
        fetchUser,
        saveUser,
        updatePassword,
    };
}

export function ProviderUsers({ children }: { children: React.ReactNode }) {
    const value = useProviderUsers();
    return <usersListContext.Provider value={value}>{children}</usersListContext.Provider>;
}

export const useUsers = () => useContext(usersListContext);

export function ProviderUserDetail({
    children,
    userId,
}: {
    children: React.ReactNode;
    userId: number | null | undefined;
}) {
    const value = useProviderUserDetail(userId);
    return <userDetailContext.Provider value={value}>{children}</userDetailContext.Provider>;
}

export const useUserDetail = () => useContext(userDetailContext);
